/**
 * quests.ts — contracts. The system offers eligible contracts over time; the
 * player fulfils them by delivering (consuming) ore for money + research rewards.
 */
import type { GameState, ActiveQuest } from '../core/GameState';
import type { EventBus } from '../core/EventBus';
import { int } from '../core/rng';
import { BALANCE, QUESTS, QUEST_BY_ID } from '../data';
import { meetsRequirement } from './unlock';

function eligibleQuestIds(state: GameState): string[] {
  const activeIds = new Set(state.quests.active.map((q) => q.id));
  return QUESTS.filter((q) => !activeIds.has(q.id) && meetsRequirement(state, q.requires)).map(
    (q) => q.id,
  );
}

export function tickQuests(state: GameState, bus?: EventBus): void {
  if (state.stats.lifetimeEarnings < BALANCE.quests.unlockLifetime) return;
  const now = state.meta.gameTimeMs;
  if (state.quests.active.length >= BALANCE.quests.maxActive) return;
  if (now < state.quests.nextOfferAt) return;
  state.quests.nextOfferAt = now + 30_000;

  const pool = eligibleQuestIds(state);
  if (pool.length === 0) return;
  const id = pool[int(state.rng, 0, pool.length - 1)]!;
  const def = QUEST_BY_ID[id]!;
  const quest: ActiveQuest = {
    id: def.id,
    oreId: def.oreId,
    amount: def.amount,
    reward: def.reward,
    researchReward: def.researchReward ?? 0,
  };
  state.quests.active.push(quest);
  bus?.emit('quest:offered', { questId: def.id });
}

/** Can the player fulfil this active contract right now? */
export function canCompleteQuest(state: GameState, questId: string): boolean {
  const q = state.quests.active.find((a) => a.id === questId);
  if (!q) return false;
  return (state.mining.ore[q.oreId] ?? 0) >= q.amount;
}

export function completeQuest(state: GameState, questId: string, bus?: EventBus): boolean {
  const idx = state.quests.active.findIndex((a) => a.id === questId);
  if (idx < 0) return false;
  const q = state.quests.active[idx]!;
  const have = state.mining.ore[q.oreId] ?? 0;
  if (have < q.amount) return false;

  state.mining.ore[q.oreId] = have - q.amount;
  state.economy.money += q.reward;
  state.stats.lifetimeEarnings += q.reward;
  state.research.points += q.researchReward;
  state.quests.active.splice(idx, 1);
  state.quests.completed.push(q.id);

  bus?.emit('quest:completed', { questId: q.id, reward: `$${q.reward}` });
  bus?.emit('notify', { kind: 'reward', message: `Auftrag erfüllt: +$${q.reward}` });
  return true;
}

/** Abandon an active contract without reward. */
export function abandonQuest(state: GameState, questId: string): boolean {
  const idx = state.quests.active.findIndex((a) => a.id === questId);
  if (idx < 0) return false;
  state.quests.active.splice(idx, 1);
  return true;
}
